import { Controller } from "stimulus"

// Sentinel option value (kept in sync with MenuAppearancesHelper::CREATE_NEW_MENU_APPEARANCE_VALUE).
// Selecting it never submits as a real menu_appearance_id - it opens the
// shared "create new appearance" modal instead and the selection is reverted.
const CREATE_NEW_VALUE = "__new__"

// Enhances a menu-appearance <select> with select2 so each option shows the
// style name followed by a preview chip (the entity name drawn in the style's
// text colour on its background colour). Colours come from each option's
// data-background-colour / data-text-colour attributes. The native <select>
// stays in sync, so plain form submits and value reads keep working.
export default class extends Controller {
  connect() {
    // Never let the field rest on the sentinel. If the browser defaulted the
    // <select> to "+ Create New" (its first option), fall back to "None" so the
    // stored/submitted value is real and re-picking the sentinel is a change.
    if (this.element.value === CREATE_NEW_VALUE) {
      this.element.value = ""
    }
    this.previousValue = this.element.value

    this.handleChange = this.handleChange.bind(this)
    this.handleSelect2Select = this.handleSelect2Select.bind(this)
    this.element.addEventListener("change", this.handleChange)

    if (typeof $ === "undefined" || !$.fn || !$.fn.select2) {
      return
    }

    this.$select = $(this.element)
    if (this.$select.hasClass("select2-hidden-accessible")) return

    const options = {
      width: "100%",
      minimumResultsForSearch: Infinity,
      templateResult: formatAppearanceOption,
      templateSelection: formatAppearanceOption,
    }

    // Inside a Bootstrap modal the dropdown must be parented to the modal, or the
    // modal's focus trap / backdrop makes it unclickable.
    const $modal = this.$select.closest(".modal")
    if ($modal.length) options.dropdownParent = $modal

    this.$select.select2(options)

    // select2:select fires every time an option is picked from the dropdown,
    // even when it equals the current value - unlike the native change event.
    // That is what makes "+ Create New" reliably trigger even when it is the
    // option already shown.
    this.$select.on("select2:select", this.handleSelect2Select)
  }

  disconnect() {
    this.element.removeEventListener("change", this.handleChange)

    if (this.$select && this.$select.hasClass("select2-hidden-accessible")) {
      this.$select.off("select2:select", this.handleSelect2Select)
      this.$select.select2("destroy")
    }
  }

  // Native change path. When select2 is active, select2:select drives sentinel
  // handling on user picks; but native change also fires on programmatic
  // .val().trigger('change') (e.g. the drawer prefilling the field), so keep
  // previousValue in sync for those without re-running the sentinel logic.
  handleChange() {
    if (this.$select && this.$select.hasClass("select2-hidden-accessible")) {
      if (this.element.value !== CREATE_NEW_VALUE) this.previousValue = this.element.value
      return
    }
    console.log("[appearance-swatch] native change fired", { value: this.element.value })
    this.handleSelection(this.element.value)
  }

  handleSelect2Select(event) {
    const value = (event.params && event.params.data && event.params.data.id) || this.element.value
    console.log("[appearance-swatch] select2:select fired", { value })
    this.handleSelection(value)
  }

  // "+ Create New" was picked: revert the select to whatever it held before,
  // then hand off to the shared create-appearance modal. The modal appends a
  // real option and re-selects the field once the appearance is saved.
  handleSelection(value) {
    if (value !== CREATE_NEW_VALUE) {
      this.previousValue = value
      return
    }

    console.log("[appearance-swatch] sentinel picked - reverting to", this.previousValue, "and dispatching create-requested")
    this.setSelectValue(this.previousValue)

    document.dispatchEvent(
      new CustomEvent("menu-appearance:create-requested", {
        detail: { label: previewLabel(this.element), select: this.element },
      }),
    )
    console.log("[appearance-swatch] create-requested dispatched")
  }

  setSelectValue(value) {
    this.previousValue = value

    if (this.$select && this.$select.hasClass("select2-hidden-accessible")) {
      this.$select.val(value).trigger("change")
    } else {
      this.element.value = value
    }
  }
}

// Label shown inside the preview chip: the name of the entity being styled
// (menu / category / item). Static via data-preview-label, or read live from
// another field via data-preview-label-source. Falls back to "Preview".
function previewLabel(selectEl) {
  if (!selectEl) return "Preview"

  const staticLabel = selectEl.getAttribute("data-preview-label")
  if (staticLabel && staticLabel.trim()) return staticLabel.trim()

  const sourceSelector = selectEl.getAttribute("data-preview-label-source")
  if (sourceSelector) {
    const scope = selectEl.form || document
    const source = scope.querySelector(sourceSelector)
    if (source && source.value && source.value.trim()) return source.value.trim()
  }

  return "Preview"
}

// A mini preview chip: a box filled with the appearance's background colour
// containing the entity name in its text colour, mirroring the preview on the
// menu appearance page.
function previewBox(background, text, label) {
  return $("<span></span>")
    .text(label)
    .css({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: "8px",
      padding: "2px 10px",
      minWidth: "52px",
      borderRadius: "4px",
      border: "1px solid rgba(0, 0, 0, 0.25)",
      backgroundColor: background || "transparent",
      color: text || "inherit",
      fontSize: "12px",
      fontWeight: "600",
      lineHeight: "1.4",
    })
}

function formatAppearanceOption(state) {
  if (!state.id) return state.text

  const option = state.element
  const background = option && option.getAttribute("data-background-colour")
  const text = option && option.getAttribute("data-text-colour")
  if (!background && !text) return state.text

  const label = previewLabel(option && option.closest("select"))

  const $wrap = $("<span></span>").css({
    display: "inline-flex",
    alignItems: "center",
  })
  $wrap.append($("<span></span>").text(state.text))
  $wrap.append(previewBox(background, text, label))
  return $wrap
}
